import { useParams, Link } from 'react-router';
import { trpc } from '../trpc/client';
import { useStore } from '../store/useStore';
import {
  Star, Heart, Share2, Shield, Truck, Clock, Award, Zap,
  ChevronRight, Minus, Plus, CheckCircle, Globe,
  FileText, MessageSquare, AlertTriangle
} from 'lucide-react';
import { useState } from 'react';

export default function DealDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { user, currency, addToCart } = useStore();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'description' | 'specifications' | 'delivery' | 'reviews'>('description');

  const { data: deal, isLoading } = trpc.deal.getBySlug.useQuery(
    { slug: slug || '' },
    { enabled: !!slug }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-3 border-[#c2410c] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-[#1e293b] mb-2">Deal Not Found</h2>
          <p className="text-[#64748b] mb-4">The deal you're looking for doesn't exist or has been removed.</p>
          <Link to="/deals" className="text-[#c2410c] font-medium hover:underline">Browse all deals</Link>
        </div>
      </div>
    );
  }

  const mainImage = deal.images?.[0] || '';
  const supplierPrice = Number(deal.supplierPrice);
  const buyerPrice = Number(deal.buyerPrice);
  const marginAmount = buyerPrice - supplierPrice;

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 py-4 text-sm">
          <Link to="/" className="text-[#64748b] hover:text-[#c2410c]">Home</Link>
          <ChevronRight className="w-4 h-4 text-[#cbd5e1]" />
          <Link to="/deals" className="text-[#64748b] hover:text-[#c2410c]">Deals</Link>
          <ChevronRight className="w-4 h-4 text-[#cbd5e1]" />
          <Link to={`/deals?category=${deal.categoryId}`} className="text-[#64748b] hover:text-[#c2410c]">{deal.category?.name}</Link>
          <ChevronRight className="w-4 h-4 text-[#cbd5e1]" />
          <span className="text-[#1e293b] font-medium truncate max-w-[200px]">{deal.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div>
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 mb-4 aspect-[4/3]">
              <img src={mainImage} alt={deal.name} className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-4 gap-3">
              {deal.images?.map((img: string, i: number) => (
                <div key={i} className="rounded-xl overflow-hidden border border-gray-100 aspect-square cursor-pointer hover:border-[#c2410c] transition-colors">
                  <img src={img} alt={`${deal.name} ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Deal Info */}
          <div>
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 bg-[#c2410c]/10 text-[#c2410c] text-xs font-semibold rounded-full flex items-center gap-1">
                <Award className="w-3 h-3" /> {deal.supplier?.verificationBadge === 'gold_verified' ? 'Gold Verified' : 'Verified Supplier'}
              </span>
              {deal.featured && (
                <span className="px-3 py-1 bg-[#4338ca]/10 text-[#4338ca] text-xs font-semibold rounded-full flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Featured
                </span>
              )}
              {deal.insuranceRequired && (
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Insured
                </span>
              )}
              <span className="px-3 py-1 bg-gray-100 text-[#64748b] text-xs font-semibold rounded-full capitalize">
                {deal.dealType}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-semibold text-[#1e293b] mb-3 font-display">{deal.name}</h1>
            
            {/* Rating */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-1 text-amber-500">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className={`w-5 h-5 ${star <= Math.round(Number(deal.avgRating)) ? 'fill-current' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-sm font-semibold text-[#1e293b]">{deal.avgRating}</span>
              <span className="text-sm text-[#64748b]">({(deal.reviews?.length || 0)} reviews)</span>
              <span className="text-[#cbd5e1]">|</span>
              <span className="text-sm text-[#64748b]">{deal.totalOrders} orders</span>
            </div>

            {/* Pricing */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <p className="text-xs text-[#94a3b8] mb-1">IPO Final Price (incl. 20% margin)</p>
                  <p className="text-4xl font-bold text-[#1e293b] font-display">{currency === 'USD' ? '$' : '€'}{buyerPrice.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#94a3b8] mb-1">Supplier Price</p>
                  <p className="text-lg text-[#64748b] line-through">{currency === 'USD' ? '$' : '€'}{supplierPrice.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-[#f8fafc] rounded-lg text-xs text-[#64748b]">
                <Shield className="w-4 h-4 text-emerald-600" />
                IPO adds {deal.marginPercent}% margin: {currency === 'USD' ? '$' : '€'}{marginAmount.toLocaleString()} covers escrow, compliance, affiliate commissions, and platform operations
              </div>
            </div>

            {/* Supplier Info */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#312e81] to-[#c2410c] flex items-center justify-center text-white font-bold text-lg">
                  {deal.supplier?.companyName?.[0]}
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e293b]">{deal.supplier?.companyName}</h3>
                  <p className="text-xs text-[#64748b]">{deal.supplier?.totalSales} sales · {deal.supplier?.supplierPoints} points</p>
                </div>
                <Link to="#" className="ml-auto text-xs text-[#c2410c] font-medium hover:underline">View Profile</Link>
              </div>
            </div>

            {/* Quantity + Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 py-3 text-sm font-semibold min-w-[48px] text-center">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => addToCart(deal.id, quantity)}
                className="flex-1 px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold shadow-sm"
              >
                Add to Cart — {currency === 'USD' ? '$' : '€'}{(buyerPrice * quantity).toLocaleString()}
              </button>
              <button className="px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                <Heart className="w-5 h-5 text-[#64748b]" />
              </button>
              <button className="px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                <Share2 className="w-5 h-5 text-[#64748b]" />
              </button>
            </div>

            {/* Trust Signals */}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="flex items-center gap-2 text-xs text-[#64748b]">
                <Shield className="w-4 h-4 text-emerald-600" /> Escrow Protected
              </div>
              <div className="flex items-center gap-2 text-xs text-[#64748b]">
                <Truck className="w-4 h-4 text-[#4338ca]" /> Delivery: {deal.deliveryTimeDays} days
              </div>
              <div className="flex items-center gap-2 text-xs text-[#64748b]">
                <Clock className="w-4 h-4 text-amber-600" /> Warranty: {deal.warrantyPeriodDays} days
              </div>
              <div className="flex items-center gap-2 text-xs text-[#64748b]">
                <CheckCircle className="w-4 h-4 text-emerald-600" /> Verified Supplier
              </div>
            </div>

            {/* Affiliate Link */}
            {user && (
              <div className="bg-gradient-to-r from-[#312e81]/5 to-[#c2410c]/5 rounded-2xl border border-[#312e81]/10 p-4">
                <p className="text-xs font-semibold text-[#312e81] mb-1.5 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5" /> Earn Commission
                </p>
                <p className="text-xs text-[#64748b] mb-2">Share this deal and earn up to 15% of the IPO margin when someone purchases through your link.</p>
                <button className="text-xs font-medium text-[#c2410c] hover:underline">Generate Referral Link</button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-12">
          <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-100 w-fit mb-6">
            {(['description', 'specifications', 'delivery', 'reviews'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium capitalize transition-colors ${activeTab === tab ? 'bg-[#0f172a] text-white' : 'text-[#64748b] hover:text-[#1e293b]'}`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <p className="text-[#1e293b] leading-relaxed whitespace-pre-line">{deal.description}</p>
                {deal.returnPolicy && (
                  <div className="mt-6 p-4 bg-[#f8fafc] rounded-xl">
                    <h4 className="text-sm font-semibold text-[#1e293b] mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Return Policy
                    </h4>
                    <p className="text-sm text-[#64748b]">{deal.returnPolicy}</p>
                  </div>
                )}
                {deal.warrantyDetails && (
                  <div className="mt-4 p-4 bg-[#f8fafc] rounded-xl">
                    <h4 className="text-sm font-semibold text-[#1e293b] mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Warranty
                    </h4>
                    <p className="text-sm text-[#64748b]">{deal.warrantyDetails}</p>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'specifications' && (
              <div>
                {deal.specifications ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(deal.specifications as Record<string, string>).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-3 border-b border-gray-100">
                        <span className="text-sm text-[#64748b] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="text-sm font-medium text-[#1e293b]">{value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#64748b]">No specifications available.</p>
                )}
              </div>
            )}
            {activeTab === 'delivery' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-[#f8fafc] rounded-xl">
                    <h4 className="text-sm font-semibold text-[#1e293b] mb-2 flex items-center gap-2">
                      <Truck className="w-4 h-4" /> Delivery Type
                    </h4>
                    <p className="text-sm text-[#64748b] capitalize">{deal.deliveryType?.replace('_', ' ')}</p>
                  </div>
                  <div className="p-4 bg-[#f8fafc] rounded-xl">
                    <h4 className="text-sm font-semibold text-[#1e293b] mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Estimated Time
                    </h4>
                    <p className="text-sm text-[#64748b]">{deal.deliveryTimeDays} business days</p>
                  </div>
                </div>
                <h4 className="text-sm font-semibold text-[#1e293b] mb-3">Available Delivery Zones</h4>
                <div className="space-y-2">
                  {deal.deliveryZones?.map((zone: any) => (
                    <div key={zone.id} className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#4338ca]" />
                        <span className="text-sm text-[#1e293b] capitalize">
                          {zone.zoneType === 'global' ? 'Global' : zone.countryCode || zone.continent || zone.region || zone.city}
                        </span>
                      </div>
                      <span className="text-xs text-[#64748b]">
                        {zone.deliveryCostIncluded ? 'Included' : `+$${zone.additionalDeliveryCost}`}
                      </span>
                    </div>
                  ))}
                </div>
                {deal.insuranceRequired && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-amber-800">Insurance Required</h4>
                      <p className="text-sm text-amber-700">{deal.insuranceDetails}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'reviews' && (
              <div>
                {deal.reviews && deal.reviews.length > 0 ? (
                  <div className="space-y-4">
                    {deal.reviews.map((review: any) => (
                      <div key={review.id} className="p-4 bg-[#f8fafc] rounded-xl">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-1 text-amber-500">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'fill-current' : 'text-gray-300'}`} />
                            ))}
                          </div>
                          <span className="text-xs text-[#64748b]">Verified Purchase</span>
                        </div>
                        {review.title && <h4 className="text-sm font-semibold text-[#1e293b] mb-1">{review.title}</h4>}
                        <p className="text-sm text-[#64748b]">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-[#64748b]">No reviews yet. Be the first to review after purchase!</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
